import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const { t } = useTranslation();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const quickLinks = [
    { label: t('nav.agenda'), href: '/agenda' },
    { label: t('nav.histoire'), href: '/histoire' },
    { label: t('nav.videos'), href: '/videos' },
    { label: t('nav.ideologie'), href: '/ideologie' },
    { label: t('nav.booking'), href: '/booking' },
    { label: 'App', href: '/app' },
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://instagram.com/invasionlatina', label: 'Instagram' },
    { icon: Facebook, href: 'https://facebook.com/invasionlatina', label: 'Facebook' },
    { icon: TikTokIcon, href: 'https://tiktok.com/@invasionlatina', label: 'TikTok' },
    { icon: Youtube, href: 'https://youtube.com/@invasionlatina', label: 'YouTube' },
  ];

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email: newsletterEmail });
      toast.success(t('footer.newsletter_success'));
      setNewsletterEmail('');
    } catch (error) {
      console.error('Newsletter error:', error);
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <footer className="bg-[#0B0B0C] border-t border-white/10 py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-3">
              <img
                src="/images/logo.png"
                alt="Invasion Latina"
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-xs text-[#B9B2A8] tracking-wider uppercase">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-4 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#B9B2A8] hover:text-[#C9A84C] hover:border-[#C9A84C]/30 transition-colors"
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-wider text-[#C9A84C] mb-4">
              {t('footer.links')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-[#B9B2A8] hover:text-[#F5F0E8] transition-colors"
                    data-testid={`footer-link-${link.href.replace('/', '')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-wider text-[#C9A84C] mb-4">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:info@invasionlatina.be"
                  className="flex items-center gap-2 text-sm text-[#B9B2A8] hover:text-[#F5F0E8] transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4" />
                  info@invasionlatina.be
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/32478814497"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-[#B9B2A8] hover:text-[#F5F0E8] transition-colors"
                  data-testid="footer-whatsapp"
                >
                  <Phone className="w-4 h-4" />
                  +32 478 81 44 97
                </a>
              </li>
              <li>
                <span className="flex items-center gap-2 text-sm text-[#B9B2A8]">
                  <MapPin className="w-4 h-4" />
                  Bruxelles, Belgique
                </span>
              </li>
            </ul>
          </div>

          {/* App Links */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-wider text-[#C9A84C] mb-4">
              App
            </h4>
            <div className="flex flex-col gap-3">
              <a
                href="https://apps.apple.com/us/app/invasion-latina/id6758810207"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-[#F5F0E8] hover:border-[#C9A84C]/30 transition-colors"
                data-testid="footer-app-store"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.invasionlatina.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-[#F5F0E8] hover:border-[#C9A84C]/30 transition-colors"
                data-testid="footer-google-play"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                Google Play
              </a>
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <h4 className="font-display text-sm uppercase tracking-wider text-[#C9A84C]">
              {t('footer.newsletter')}
            </h4>
            <form onSubmit={handleNewsletterSubmit} className="flex w-full sm:w-auto gap-2">
              <input
                type="email"
                required
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder={t('footer.newsletter_placeholder')}
                className="flex-1 sm:w-64 px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-[#F5F0E8] placeholder:text-[#B9B2A8]/50 focus:outline-none focus:border-[#C9A84C]/50"
              />
              <button
                type="submit"
                disabled={newsletterLoading}
                className="px-6 py-2.5 bg-[#C9A84C] text-[#060606] rounded-lg text-sm font-display uppercase tracking-wider hover:bg-[#E2C56A] transition-colors disabled:opacity-50"
              >
                {newsletterLoading ? '...' : t('footer.newsletter_submit')}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-8 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-[#B9B2A8]">
              &copy; {new Date().getFullYear()} Invasion Latina. {t('footer.rights')}.
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <Link
                to="/privacy"
                className="text-xs text-[#B9B2A8] hover:text-[#F5F0E8] transition-colors"
                data-testid="footer-privacy"
              >
                {t('footer.privacy')}
              </Link>
              <Link
                to="/terms"
                className="text-xs text-[#B9B2A8] hover:text-[#F5F0E8] transition-colors"
                data-testid="footer-terms"
              >
                {t('footer.terms')}
              </Link>
              <Link
                to="/support"
                className="text-xs text-[#B9B2A8] hover:text-[#F5F0E8] transition-colors"
                data-testid="footer-support"
              >
                {t('footer.support')}
              </Link>
              <Link
                to="/es"
                className="text-xs text-[#B9B2A8] hover:text-[#F5F0E8] transition-colors"
                data-testid="footer-espanol"
                hrefLang="es"
                title="Versión en español"
              >
                🇪🇸 Español
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
